import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { isAdminUser } from '@/utils/user';
import { DeleteModal } from '@/components/DeleteModal';
import { Button, CardActions, Grid, InputAdornment, MenuItem, Paper, Select, Stack, TextField, IconButton, Typography, Tooltip } from '@mui/material';
import { DeckCard } from '@/screens/decks/DeckCard';
import { AddDeckDrawer } from '@/screens/decks/AddDeckDrawer';
import { enqueueSnackbar } from 'notistack';
import { DeckDTO, UserDTO } from '@/generated';
import { useUser } from '@/hooks/use-user.hook';
import { UpdateDeckModal } from './UpdateDeckModal';
import { DefaultContainer } from '@/components/DefaultContainer';
import DeleteIcon from '@mui/icons-material/Delete';
import { Add, AssignmentAdd, AssignmentReturn, FilterList, Search, ViewCompact, ViewCompactOutlined, ViewList, ViewListOutlined } from '@mui/icons-material';
import { DeckListElement } from './DeckListElement';
import { AppContext } from '@/contexts/app.context';
import { useListSpecificUserDecks, useListUserDecksQuery } from '@/hooks/query-hooks';
import { useAssignDeckToUser, useDeleteDeckMutation, useUnassignDeckFromUser } from '@/hooks/deck-mutation.hooks';

export enum DisplayMode {
  Card,
  List,
}

const getShowDecksDisplayMode = () => localStorage.getItem('decksDisplayMode');
const setShowDecksDisplayMode = (displayMode: DisplayMode) => localStorage.setItem('decksDisplayMode', String(displayMode));

interface DeckGridElementProps {
  deckList: Array<DeckDTO>;
  deck: DeckDTO;
  user?: UserDTO;
  userId?: string;
  displayMode: DisplayMode;
  refreshUser: () => void;
}

const DeckGridElement = (props: DeckGridElementProps) => {
  const { user, deck, deckList, refreshUser, userId, displayMode } = props;
  const context = useContext(AppContext);
  const localUser = context.state?.userProfile;
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const isDeckCreator = () => props.deck.creatorId === localUser?.userId;
  const isEditor = () => props.deck.editorIds?.includes(localUser?.userId ?? '');
  const deleteDeckMutation = useDeleteDeckMutation();
  const assignDeckMutation = useAssignDeckToUser();
  const unassignDeckMutation = useUnassignDeckFromUser();

  return (
    <>
      <DeleteModal
        show={showDeleteModal}
        setShow={setShowDeleteModal}
        nameOfObjectToDelete="Deck"
        handleDelete={async () => {
          await deleteDeckMutation.mutateAsync({ deckId: deck.id });
          enqueueSnackbar(`Deck ${deck.name} deleted!`, { variant: 'success' });
          setShowDeleteModal(false);
        }}
      ></DeleteModal>
      {displayMode === DisplayMode.Card && (
        <Grid size={{ xs: 12, md: 4 }}>
          <DeckCard isEditor={isEditor()} timeout={1000} deck={deck} isDeckCreator={isDeckCreator()}>
            <CardActions sx={{ justifyContent: 'flex-end', alignSelf: 'flex-end', flex: 0.1 }}>
              {(isDeckCreator() || isAdminUser(localUser)) && userId && deckList.find((s) => s.id === deck.id) !== undefined && (
                <Tooltip title={`Unassign ${deck.name} from user`}>
                  <IconButton
                    onClick={async () => {
                      await unassignDeckMutation.mutateAsync({ deckId: deck.id, userId });
                      enqueueSnackbar('Deck successfully unassigned from user', { variant: 'success' });
                      refreshUser();
                    }}
                  >
                    <AssignmentReturn />
                  </IconButton>
                </Tooltip>
              )}

              {isAdminUser(localUser) && user && !user?.deckIds.includes(deck.id) && (
                <Tooltip title={`Assign ${deck.name} to this User`}>
                  <IconButton
                    onClick={async () => {
                      await assignDeckMutation.mutateAsync({ deckId: deck.id, userId: user.userId });
                      enqueueSnackbar('Deck successfully assigned to user', { variant: 'success' });
                      refreshUser();
                    }}
                  >
                    <AssignmentAdd />
                  </IconButton>
                </Tooltip>
              )}
              {(isDeckCreator() || isAdminUser(localUser)) && !user ? (
                <Tooltip title={`Delete ${deck.name}`}>
                  <IconButton
                    sx={{ color: 'white' }}
                    onClick={() => {
                      const ownDeckCount = deckList?.filter((s) => s.creatorId === localUser?.userId).length || 0;

                      if (ownDeckCount > 1) {
                        setShowDeleteModal(true);
                      } else {
                        enqueueSnackbar('You cannot delete your last Deck.', { variant: 'error' });
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <></>
              )}
            </CardActions>
          </DeckCard>
        </Grid>
      )}
      {displayMode === DisplayMode.List && (
        <DeckListElement isSetCreator={isDeckCreator()} isEditor={isEditor()} deck={deck} timeout={10} setShowDeleteModal={setShowDeleteModal} />
      )}
    </>
  );
};

const getFilterOwner = () => localStorage.getItem('filterOwner') ?? 'all';
const setFilterOwnerLocalStorage = (owner: string) => localStorage.setItem('filterOwner', owner);

export const Decks = () => {
  const context = useContext(AppContext);
  const { userId } = useParams();
  const [user, error, refreshUser] = useUser(userId);

  const [filteredDecks, setFilteredDecks] = useState<Array<DeckDTO>>([]);

  const [filterOwner, setFilterOwner] = useState<string>(getFilterOwner());
  const [filterDeckName, setFilterDeckName] = useState<string>();

  const [showCreateDeckModal, setShowCreateDeckModal] = useState(false);
  const [showAssignDeckDrawer, setShowAssignDeckDrawer] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const { decks: selectedUserDecks } = useListSpecificUserDecks(userId);
  const { decks: userDecks } = useListUserDecksQuery(userId === undefined);

  const [displayMode, setDisplayMode] = useState(Number(getShowDecksDisplayMode() ?? DisplayMode.Card));

  const userSelectionMode = userId !== undefined;
  let deckList: Array<DeckDTO> = [];
  if (userId) deckList = selectedUserDecks ?? [];
  else deckList = userDecks ?? [];

  useEffect(() => {
    if (deckList) {
      let filtered = deckList;
      const loggedInUserId = context.state?.userProfile?.userId;
      if (filterOwner === 'mine') filtered = deckList.filter((d) => d.creatorId === loggedInUserId && (d.ownerId === undefined || d.ownerId === loggedInUserId));
      if (filterOwner === 'others') filtered = deckList.filter((d) => d.ownerId !== undefined && d.ownerId !== loggedInUserId);

      if (filterDeckName) filtered = filtered.filter((s) => s.name.toLowerCase().includes(filterDeckName.toLowerCase()));
      setFilteredDecks(filtered);
    }
  }, [deckList, filterOwner, filterDeckName, userSelectionMode]);

  return (
    <>
      <DefaultContainer>
        {userId && <AddDeckDrawer setShow={setShowAssignDeckDrawer} show={showAssignDeckDrawer} userId={userId} />}
        <Paper sx={{ my: 1, zIndex: 1, padding: 1, position: 'sticky', top: { xs: 60, sm: 65 } }}>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center">
              <DeckPrimaryAction
                userSelectionMode={userSelectionMode}
                onCreate={() => setShowCreateDeckModal(true)}
                onAssign={() => setShowAssignDeckDrawer(true)}
              />
              <Stack sx={{ display: { xs: 'none', sm: 'flex' }, ml: 1 }} direction="row" alignItems="center" spacing={1}>
                {!userSelectionMode && <DeckOwnerFilter filterOwner={filterOwner} setFilterOwner={setFilterOwner} />}
                <DeckNameFilter filterDeckName={filterDeckName} setFilterDeckName={setFilterDeckName} />
              </Stack>
              <DeckDisplayModeButtons displayMode={displayMode} setDisplayMode={setDisplayMode} />
            </Stack>

            <Stack sx={{ display: { xs: 'flex', sm: 'none' } }} direction="row" spacing={1}>
              <DeckNameFilter filterDeckName={filterDeckName} setFilterDeckName={setFilterDeckName} mobile />
              {!userSelectionMode && (
                <Button
                  variant={showMobileFilters ? 'contained' : 'outlined'}
                  startIcon={<FilterList />}
                  onClick={() => setShowMobileFilters((show) => !show)}
                  aria-expanded={showMobileFilters}
                  aria-controls="mobile-deck-filters"
                >
                  Filters
                </Button>
              )}
            </Stack>

            {!userSelectionMode && (
              <Stack id="mobile-deck-filters" sx={{ display: { xs: showMobileFilters ? 'flex' : 'none', sm: 'none' } }}>
                <DeckOwnerFilter filterOwner={filterOwner} setFilterOwner={setFilterOwner} mobile />
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'block', sm: 'none' } }}>
              {filteredDecks.length} deck(s)
            </Typography>
          </Stack>
        </Paper>

        <Grid container direction={'row'} spacing={1}>
          {filteredDecks.map((s: DeckDTO, i) => (
            <DeckGridElement displayMode={displayMode} refreshUser={refreshUser} userId={userId} user={user} key={s.id} deck={s} deckList={filteredDecks} />
          ))}
        </Grid>
        <UpdateDeckModal deck={{ name: 'New Deck' }} show={showCreateDeckModal} setShowCreateDeckModal={setShowCreateDeckModal} />
      </DefaultContainer>
    </>
  );
};

const DeckPrimaryAction = (props: { userSelectionMode: boolean; onCreate: () => void; onAssign: () => void }) =>
  props.userSelectionMode ? (
    <Tooltip title={'Open "Assign Deck to user" side-menu'}>
      <IconButton onClick={props.onAssign}>
        <AssignmentAdd />
      </IconButton>
    </Tooltip>
  ) : (
    <Tooltip title="Create new Deck">
      <IconButton onClick={props.onCreate}>
        <Add />
      </IconButton>
    </Tooltip>
  );

const DeckNameFilter = (props: { filterDeckName?: string; setFilterDeckName: (name: string) => void; mobile?: boolean }) => (
  <TextField
    size="small"
    label="Deck name"
    value={props.filterDeckName ?? ''}
    onChange={(event) => props.setFilterDeckName(event.target.value)}
    sx={{ flex: props.mobile ? 1 : 'initial', minWidth: 0 }}
    slotProps={{
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        ),
      },
    }}
  />
);

const DeckOwnerFilter = (props: { filterOwner: string; setFilterOwner: (owner: string) => void; mobile?: boolean }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography>Owner</Typography>
    <Select
      size="small"
      fullWidth={props.mobile}
      value={props.filterOwner.length ? props.filterOwner : 'all'}
      onChange={(event) => {
        props.setFilterOwner(event.target.value);
        setFilterOwnerLocalStorage(event.target.value);
      }}
    >
      <MenuItem value="all">All</MenuItem>
      <MenuItem value="mine">Mine</MenuItem>
      <MenuItem value="others">Others</MenuItem>
    </Select>
  </Stack>
);

const DeckDisplayModeButtons = (props: { displayMode: DisplayMode; setDisplayMode: (displayMode: DisplayMode) => void }) => (
  <Stack sx={{ flex: 1, justifyContent: 'flex-end' }} direction="row">
    <IconButton
      aria-label="List view"
      onClick={() => {
        setShowDecksDisplayMode(DisplayMode.List);
        props.setDisplayMode(DisplayMode.List);
      }}
    >
      {props.displayMode === DisplayMode.List ? <ViewList /> : <ViewListOutlined />}
    </IconButton>
    <IconButton
      aria-label="Card view"
      onClick={() => {
        setShowDecksDisplayMode(DisplayMode.Card);
        props.setDisplayMode(DisplayMode.Card);
      }}
    >
      {props.displayMode === DisplayMode.Card ? <ViewCompact /> : <ViewCompactOutlined />}
    </IconButton>
  </Stack>
);
